import React, { useRef, useEffect, useMemo, useState } from "react";
import * as Chart from "chart.js";
import moment from "moment-hijri";
import HeadingComponent from "../shared/heading";

// ✅ Reusable Component with Ramadan Detection + DARK MODE SUPPORT + SHADOWS
const ChartTableComponent = ({
  title,
  subtitle,
  in_chart_content_line_1,
  datasets,
  months,
  yearRanges,
  yAxisTitle = "Average Daily Deployed",
  yMin = null,
  yMax = null,
  showBars = false,
  ramadanRevenueData = [],
  barDatasets = [],
  showSecondTable = false,
  secondTableData = null,
  showRamadanRow = false,
  ramadanborderColor = "#FFA500",
}) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const [themeKey, setThemeKey] = useState(0);

  // ✅ Store value label rects for Ramadan avoidance
  const valueLabelRects = useRef([]);
  const containerRef = useRef(null);
  const tableRef = useRef(null);
  const labelColRef = useRef(null);

  // ✅ Detect theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setThemeKey((prev) => prev + 1);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  // ✅ Ramadan Detection Function (returns array with month indices)
  const getRamadanMonths = useMemo(() => {
    if (!showRamadanRow || !months || !yearRanges) return [];

    const ramadanMonthIndices = [];
    let currentMonthIndex = 0;

    yearRanges.forEach((yearRange) => {
      const year = parseInt(yearRange.year);
      for (let i = 0; i < yearRange.span; i++) {
        const monthName = months[currentMonthIndex];
        const monthMap = {
          Jan: 0, Feb: 1, Mar: 2, Apr: 3,
          May: 4, Jun: 5, Jul: 6, Aug: 7,
          Sep: 8, Oct: 9, Nov: 10, Dec: 11,
        };
        const monthNum =
          monthMap[monthName] !== undefined
            ? monthMap[monthName]
            : currentMonthIndex % 12;
        const hijriYear = year - 579;
        const ramadanFirstDay = moment(`${hijriYear}-9-1`, 'iYYYY-iM-iD');
        const ramadanStartGregMonth = ramadanFirstDay.month();
        const ramadanStartGregYear = ramadanFirstDay.year();

        if (
          ramadanStartGregYear === year &&
          ramadanStartGregMonth === monthNum
        ) {
          ramadanMonthIndices.push(currentMonthIndex);
        }
        currentMonthIndex++;
      }
    });

    return ramadanMonthIndices;
  }, [months, yearRanges, showRamadanRow]);

  // ✅ Auto-calculate Y-axis range from data
  const calculateYAxisRange = () => {
    const allValues = [];
    datasets.forEach((ds) => {
      ds.data.forEach((val) => {
        if (typeof val === "number" && !isNaN(val)) allValues.push(val);
      });
    });
    if (showBars && barDatasets.length > 0) {
      barDatasets.forEach((ds) => {
        ds.data.forEach((val) => {
          if (Array.isArray(val)) {
            val.forEach((v) => {
              if (typeof v === "number" && !isNaN(v)) allValues.push(v);
            });
          } else if (typeof val === "number" && !isNaN(val)) {
            allValues.push(val);
          }
        });
      });
    }
    if (allValues.length === 0) return { min: 0, max: 100 };
    const dataMin = Math.min(...allValues);
    const dataMax = Math.max(...allValues);
    let padding;
    if (dataMax <= 2) {
      padding = (dataMax - dataMin) * 1;
    } else {
      padding = (dataMax - dataMin) * 0.25;
    }
    return {
      min: Math.max(0, Math.round(dataMin - padding)),
      max: Math.round(dataMax + padding),
    };
  };

  const yAxisRange = {
    min: yMin !== null ? yMin : calculateYAxisRange().min,
    max: yMax !== null ? yMax : calculateYAxisRange().max,
  };

  useEffect(() => {
    if (!chartRef.current) return;

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    const existingChart = Chart.Chart.getChart(chartRef.current);
    if (existingChart) {
      existingChart.destroy();
    }

    const ctx = chartRef.current.getContext("2d");

    const textColor = "#e5e7eb";
    const labelTextColor = "#ffffff";
    // const BOX_BG = "rgba(0, 0, 0, 0)";

    const BOX_BG = "#464647";


    // ✅ This will be populated by dataLabelsPlugin and used by ramadanPlugin
    const finalLabelRects = [];

    // ✅ Value labels are computed here first, but NOT drawn immediately —
    // ramadanPlugin gets first pick of its natural spot, and only then do we
    // nudge any clashing value label out of the way, before finally drawing
    // the value labels (drawValueLabelsPlugin) on top.
    const pendingValueLabels = [];
    // ✅ Ramadan box specs get computed/nudged first, then drawn LAST (see
    // drawRamadanBoxesPlugin) so they always render on top of everything.
    const pendingRamadanBoxes = [];

    // ✅ LINE SHADOW PLUGIN
    const lineShadowPlugin = {
      id: "lineShadow",
      beforeDatasetDraw(chart, args) {
        const dataset = chart.data.datasets[args.index];
        if (dataset.type === "bar") return;
        const ctx = chart.ctx;
        ctx.save();
        ctx.shadowColor = dataset.borderColor || "rgba(255,255,255,0.8)";
        ctx.shadowBlur = 18;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      },
      afterDatasetDraw(chart, args) {
        const dataset = chart.data.datasets[args.index];
        if (dataset.type === "bar") return;
        chart.ctx.restore();
      },
    };

    // ✅ BAR SHADOW PLUGIN
    const barShadowPlugin = {
      id: "barShadow",
      beforeDatasetDraw(chart, args) {
        const dataset = chart.data.datasets[args.index];
        if (dataset.type !== "bar") return;
        const ctx = chart.ctx;
        ctx.save();
        ctx.shadowColor = "rgba(0, 0, 0, 0.55)";
        ctx.shadowBlur = 8;
        ctx.shadowOffsetX = 3;
        ctx.shadowOffsetY = 4;
      },
      afterDatasetDraw(chart, args) {
        const dataset = chart.data.datasets[args.index];
        if (dataset.type !== "bar") return;
        chart.ctx.restore();
      },
    };

    // ✅ Data Labels Plugin
    const dataLabelsPlugin = {
      id: "customDataLabels",
      afterDatasetsDraw(chart) {
        const ctx = chart.ctx;
        const fontSize = 10;
        const padding = 3;

        // Clear previous rects
        finalLabelRects.length = 0;
        pendingValueLabels.length = 0;

        const ramadanMonthIndices = new Set(getRamadanMonths);
        const labels = [];

        chart.data.datasets.forEach((dataset, i) => {
          const meta = chart.getDatasetMeta(i);
          if (meta.hidden || dataset.type === "bar") return;

          meta.data.forEach((element, index) => {
            const value = dataset.data[index];

            // Ramadan label wali exact value hide karo
            if (ramadanMonthIndices.has(index)) {
              const barVal =
                ramadanRevenueData?.[index] ??
                chart.data.datasets.find(d => d.type === "bar")?.data?.[index];

              // Woh line dataset dhundo jis ki value bar se match karti hai
              const lineDatasets = chart.data.datasets.filter(d => d.type !== "bar");
              const matchingDataset = lineDatasets.find(d => d.data[index] === barVal);

              // Sirf usi dataset ka label hide karo
              if (matchingDataset && dataset.label === matchingDataset.label) return;
            }

            if (value == null || value === 0) return;

            const x = element?.x;
            const y = element?.y;

            if (x == null || y == null || isNaN(x) || isNaN(y)) return;

            const color = dataset.borderColor;
            const lineDatasets = chart.data.datasets.filter(
              (ds) => ds.type !== "bar"
            );
            const lineIndex = lineDatasets.findIndex(
              (ds) => ds.label === dataset.label
            );
            const baseYOffset =
              lineIndex === lineDatasets.length - 1 ? 36 : -26;

            const text =
              typeof value === "number" && !isNaN(value)
                ? value.toLocaleString()
                : value?.toString() || "0";

            ctx.font = `bold ${fontSize}px Arial`;
            const textWidth = ctx.measureText(text).width;
            const boxWidth = textWidth + padding * 2;
            const boxHeight = 17;

            labels.push({
              x,
              y,
              text,
              color,
              width: boxWidth,
              height: boxHeight,
              baseY: y + baseYOffset,
              datasetIndex: i,
              pointIndex: index,
            });
          });
        });

        labels.sort((a, b) => a.x - b.x);
        labels.forEach((l) => { l.finalY = l.baseY; });

        const V_GAP = 4;  // min vertical gap between labels
        const H_GAP = 3;  // min horizontal gap

        // Returns true if two label rects overlap (with gaps)
        const rectsOverlap = (a, b) => {
          const aL = a.x - a.width / 2 - H_GAP;
          const aR = a.x + a.width / 2 + H_GAP;
          const aT = a.finalY - a.height / 2 - V_GAP;
          const aB = a.finalY + a.height / 2 + V_GAP;
          const bL = b.x - b.width / 2;
          const bR = b.x + b.width / 2;
          const bT = b.finalY - b.height / 2;
          const bB = b.finalY + b.height / 2;
          return !(aR < bL || aL > bR || aB < bT || aT > bB);
        };

        // Multi-pass resolution: each pass tries to push overlapping labels apart
        // Labels above their data point push UP, labels below push DOWN
        const MAX_PASSES = 50;
        for (let pass = 0; pass < MAX_PASSES; pass++) {
          let anyMoved = false;
          for (let i = 0; i < labels.length; i++) {
            for (let j = i + 1; j < labels.length; j++) {
              const a = labels[i];
              const b = labels[j];
              if (!rectsOverlap(a, b)) continue;

              anyMoved = true;

              // Chart bounds se bahar na jayein
              const chartT = chart.scales.y.top + 32;
              const chartB = chart.scales.y.bottom - 4;

              // ✅ Whoever is physically higher (smaller finalY) gets pushed
              // further up; whoever is lower gets pushed further down —
              // regardless of which is "a" or "b". A fixed a-above-b formula
              // here used to occasionally push two labels toward each other
              // instead of apart, when "b" actually sat above "a" in pixels.
              const hi = a.finalY <= b.finalY ? a : b;
              const lo = a.finalY <= b.finalY ? b : a;
              const overlapAmt = (hi.finalY + hi.height / 2 + V_GAP) - (lo.finalY - lo.height / 2);
              if (overlapAmt <= 0) continue;

              hi.finalY = Math.max(chartT, hi.finalY - overlapAmt / 2);
              lo.finalY = Math.min(chartB, lo.finalY + overlapAmt / 2);
            }
          }
          if (!anyMoved) break;
        }

        labels.forEach((lbl) => {
          if (lbl.finalY == null || isNaN(lbl.finalY)) return;

          const boxX = lbl.x - lbl.width / 2;
          const boxY = lbl.finalY - lbl.height / 2;

          // ✅ Push to finalLabelRects — tight top padding so Ramadan can go above value labels
          finalLabelRects.push({
            left: boxX - 6,
            right: boxX + lbl.width + 6,
            top: boxY - 2,
            bottom: boxY + lbl.height + 6,
          });

          // ✅ Don't draw yet — hand off to ramadanPlugin first so it can claim
          // its natural spot and push this label out of the way if needed.
          // drawValueLabelsPlugin (runs after ramadanPlugin) does the actual drawing.
          pendingValueLabels.push({
            x: lbl.x,
            y: lbl.y,
            finalY: lbl.finalY,
            text: lbl.text,
            color: lbl.color,
            width: lbl.width,
            height: lbl.height,
            pointIndex: lbl.pointIndex,
          });
        });
      },
    };

    // ✅ DRAW VALUE LABELS PLUGIN — draws the value-label boxes computed above,
    // AFTER ramadanPlugin has had a chance to nudge any that clash with a
    // Ramadan label out of the way. Runs after ramadanPlugin in the plugins list.
    const drawValueLabelsPlugin = {
      id: "drawValueLabels",
      afterDatasetsDraw(chart) {
        const ctx = chart.ctx;
        pendingValueLabels.forEach((lbl) => {
          if (lbl.finalY == null || isNaN(lbl.finalY)) return;

          const boxX = lbl.x - lbl.width / 2;
          const boxY = lbl.finalY - lbl.height / 2;

          ctx.save();
          ctx.fillStyle = BOX_BG;
          ctx.fillRect(boxX - 1, boxY - 1, lbl.width + 2, lbl.height + 2);
          ctx.strokeStyle = lbl.color;
          ctx.lineWidth = 1;
          ctx.strokeRect(boxX, boxY, lbl.width, lbl.height);
          ctx.fillStyle = labelTextColor;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.font = "10px Arial";
          ctx.fillText(lbl.text, lbl.x, lbl.finalY);
          ctx.restore();
        });
      },
    };


    // ✅ RAMADAN PLUGIN - Sirf Bar Dataset ki value show karega
    const ramadanPlugin = showRamadanRow
      ? {
        id: "ramadanPlugin",
        afterDatasetsDraw(chart) {
          const ctx = chart.ctx;
          const xAxis = chart.scales.x;
          const yAxis = chart.scales.y;

          pendingRamadanBoxes.length = 0;

          const ramadanMonthIndices = getRamadanMonths;
          if (ramadanMonthIndices.length === 0) return;

          const BOX_H = 20;
          const ARROW_W = 10;
          const H_PAD = 4;
          const V_PAD = 4;
          const BR = 3;

          const chartTop = yAxis.top;
          const chartBottom = yAxis.bottom;
          const chartLeft = xAxis.left;
          const chartRight = xAxis.right;

          // BOX_W is computed per-item based on actual text — see inside forEach

          const maxLabelY = chartTop + ((chartBottom - chartTop) * 0.60);

          // Chart boundary clamp — lets xOffsets actually take effect
          const clampX = (x, _anchorX, boxW) => {
            return Math.max(chartLeft + 2, Math.min(chartRight - boxW - 2, x));
          };

          const clampYAbove = (y) => {
            return Math.max(chartTop + 4, Math.min(chartBottom - BOX_H - 4, y));
          };

          const mkRect = (l, t, w, h) => ({
            left: l, right: l + w, top: t, bottom: t + h,
          });

          const overlaps = (a, b, hPad = H_PAD, vPad = V_PAD) =>
            a.left - hPad < b.right + hPad &&
            a.right + hPad > b.left - hPad &&
            a.top - vPad < b.bottom + vPad &&
            a.bottom + vPad > b.top - vPad;

          // ✅ Value labels are NOT added as obstacles here anymore — Ramadan
          // gets to sit in its natural spot (right above the bar), and any
          // value label it clashes with gets nudged out of the way afterwards
          // (see the "push clashing value labels away" step below).
          const obstacles = [];

          chart.data.datasets.forEach((dataset, dsIdx) => {
            if (dataset.type !== "bar") return;
            const meta = chart.getDatasetMeta(dsIdx);
            if (meta.hidden) return;
            meta.data.forEach((bar) => {
              if (!bar) return;
              try {
                const props = bar.getProps(["x", "y", "base", "width"], true);
                const barLeft = props.x - props.width / 2;
                const barRight = props.x + props.width / 2;
                const barTop = Math.min(props.y, props.base);
                const barBottom = Math.max(props.y, props.base);
                if (!isNaN(barLeft) && !isNaN(barTop)) {
                  obstacles.push({
                    left: barLeft - 2,
                    right: barRight + 2,
                    top: barTop - 2,
                    bottom: barBottom + 2,
                  });
                }
              } catch (_) { }
            });
          });

          chart.data.datasets.forEach((dataset, dsIdx) => {
            if (dataset.type === "bar") return;
            const meta = chart.getDatasetMeta(dsIdx);
            if (meta.hidden) return;
            meta.data.forEach((pt) => {
              if (!pt) return;
              const px = pt.x;
              const py = pt.y;
              if (px == null || py == null || isNaN(px) || isNaN(py)) return;
              obstacles.push({
                left: px - 5,
                right: px + 5,
                top: py - 5,
                bottom: py + 5,
              });
            });
          });

          const getBarTopYForIndex = (idx) => {
            for (let i = 0; i < chart.data.datasets.length; i++) {
              const meta = chart.getDatasetMeta(i);
              const dataset = chart.data.datasets[i];
              if (dataset.type === "bar" && meta?.data?.[idx]) {
                const bar = meta.data[idx];
                try {
                  const props = bar.getProps(["y", "base"], true);
                  return Math.min(props.y, props.base ?? props.y);
                } catch (_) {
                  return Math.min(bar.y, bar.base ?? bar.y);
                }
              }
            }
            for (let i = 0; i < chart.data.datasets.length; i++) {
              const meta = chart.getDatasetMeta(i);
              if (meta.hidden) continue;
              if (meta?.data?.[idx] && meta.data[idx].y !== undefined && !isNaN(meta.data[idx].y)) {
                return meta.data[idx].y;
              }
            }
            return yAxis.getPixelForValue(0);
          };

          // 🔥 FIXED: Sirf BAR dataset ki value lega (Line datasets ignore)
          const getRevenueForIndex = (idx) => {
            // 1. Check ramadanRevenueData (explicitly passed)
            if (ramadanRevenueData && ramadanRevenueData[idx] !== undefined) {
              const val = ramadanRevenueData[idx];
              if (typeof val === "number" && !isNaN(val)) return val;
              if (Array.isArray(val) && val.length > 1) {
                const numVal = parseFloat(val[1]);
                if (!isNaN(numVal)) return numVal;
              }
              return 0;
            }

            // 2. Sirf BAR datasets se value lo
            for (let i = 0; i < chart.data.datasets.length; i++) {
              const dataset = chart.data.datasets[i];
              if (dataset.type === "bar" && dataset.data && dataset.data[idx] !== undefined) {
                const val = dataset.data[idx];
                if (typeof val === "number" && !isNaN(val)) return val;
                if (Array.isArray(val) && val.length > 1) {
                  const numVal = parseFloat(val[1]);
                  if (!isNaN(numVal)) return numVal;
                }
                return 0;
              }
            }

            // 3. LINE datasets ko IGNORE karo - sirf bar ki value show hogi
            // Reusable chart jaisa behavior
            return 0;
          };

          const getMonthName = (idx) => months[idx] || "";
          const getYearForIndex = (idx) => {
            let counter = 0;
            for (const yr of yearRanges) {
              const end = counter + yr.span;
              if (idx >= counter && idx < end) return yr.year;
              counter += yr.span;
            }
            return "";
          };

          const itemsMap = new Map();

          ramadanMonthIndices.forEach((idx) => {
            const val = getRevenueForIndex(idx);
            const year = getYearForIndex(idx);
            const monthName = getMonthName(idx);

            // Sirf tabhi label show karo jab value > 0 ho
            // if (val === 0 || val === null) return;

            const key = `${year}-${monthName}`;

            if (!itemsMap.has(key)) {
              const formattedVal = typeof val === "number" && !isNaN(val) ? val.toLocaleString() : "0";
              const dataX = xAxis.getPixelForValue(idx);
              const dataY = getBarTopYForIndex(idx);
              itemsMap.set(key, {
                label: `RAMADHAN ${year} ${monthName} : ${formattedVal}`,
                dataX, dataY, monthIndex: idx, year, value: val,
              });
            }
          });

          const items = Array.from(itemsMap.values()).sort((a, b) => a.year - b.year);

          if (items.length === 0) return;

          // Place box just ABOVE the data point — relative to actual bar/line height
          const GAP_ABOVE = 10; // gap between box bottom and arrow tip
          const placedBoxes = [];
          const chartMidX = (chartLeft + chartRight) / 2;
          const chartH = chartBottom - chartTop;

          // Sort left-to-right so overlap resolution is natural
          const sortedItems = [...items].sort((a, b) => a.dataX - b.dataX);

          const isBlocked = (testRect, arrowTipY, dataXForTail) => {
            if (
              placedBoxes.some((obs) => overlaps(testRect, obs, 4, 4)) ||
              obstacles.some((obs) => overlaps(testRect, obs, 3, 3))
            ) return true;

            // ✅ Box ka tail/connector-line (box ke neeche se bar tak) bhi
            // check karo — kyunki box khud free ho sakta hai lekin uske
            // beech mein koi bar/point aa sakta hai jise tail cross kare
            const tailTop = testRect.bottom;
            const tailBottom = arrowTipY;
            if (tailBottom > tailTop) {
              const tailRect = {
                left: dataXForTail - ARROW_W / 2 - 2,
                right: dataXForTail + ARROW_W / 2 + 2,
                top: tailTop,
                bottom: tailBottom,
              };
              if (obstacles.some((obs) => overlaps(tailRect, obs, 1, 1))) return true;
            }
            return false;
          };

          sortedItems.forEach(({ label, dataX, dataY }) => {
            const arrowTipY = dataY;

            // Compute BOX_W from full single-line label text + padding
            ctx.font = "bold 10px Arial";
            const BOX_W = ctx.measureText(label).width + 20;

            // ✅ Box hamesha dataX pe horizontally centered rahega — sirf
            // upar-neeche move hoga, taake arrow hamesha seedha (vertical)
            // rahe, kabhi bhi left/right diagonal na bane.
            const step = 8;
            const MIN_GAP = GAP_ABOVE + BOX_H;
            const vertGaps = [];
            for (let d = MIN_GAP; d <= chartH - 6; d += step) vertGaps.push(Math.round(d));

            const candidateOffsets = vertGaps.map((dy) => [0, dy]);

            let chosenX = null;
            let chosenY = null;

            for (const [dx, dy] of candidateOffsets) {
              const tryX = clampX(dataX + dx - BOX_W / 2, dataX, BOX_W);
              const tryY = clampYAbove(arrowTipY - dy);
              const testRect = mkRect(tryX, tryY, BOX_W, BOX_H);
              if (!isBlocked(testRect, arrowTipY, dataX)) {
                chosenX = tryX;
                chosenY = tryY;
                break;
              }
            }

            // Fallback: least-overlap position (kabhi bhi poora free spot na mile to)
            // — yahan bhi X hamesha dataX pe hi centered rehta hai, sirf Y (height) badalti hai
            if (chosenX === null) {
              let bestScore = Infinity;
              let bestX = clampX(dataX - BOX_W / 2, dataX, BOX_W);
              let bestY = chartTop + 4;
              candidateOffsets.forEach(([dx, dy]) => {
                const bx = clampX(dataX + dx - BOX_W / 2, dataX, BOX_W);
                const by = clampYAbove(arrowTipY - dy);
                const r = mkRect(bx, by, BOX_W, BOX_H);
                const tailRect = {
                  left: dataX - ARROW_W / 2 - 2,
                  right: dataX + ARROW_W / 2 + 2,
                  top: r.bottom,
                  bottom: arrowTipY,
                };
                const score =
                  placedBoxes.filter((o) => overlaps(r, o, 0, 0)).length * 10 +
                  finalLabelRects.filter((o) => overlaps(r, o, 0, 0)).length * 5 +
                  obstacles.filter((o) => overlaps(r, o, 0, 0)).length +
                  finalLabelRects.filter((o) => overlaps(tailRect, o, 0, 0)).length * 5;
                if (score < bestScore) { bestScore = score; bestX = bx; bestY = by; }
              });
              chosenX = bestX;
              chosenY = bestY;
            }

            if (chosenX !== null && chosenY !== null) {
              const RAMADAN_COLOR = ramadanborderColor || "#ffa500";

              placedBoxes.push(mkRect(chosenX, chosenY, BOX_W, BOX_H));

              const bTop = chosenY;
              const bBot = chosenY + BOX_H;

              // ✅ Ramadan ne apni jagah le li — ab jo bhi value label iske
              // saath clash kar rahi hai, usko upar/neeche (jis taraf woh
              // already hai usi taraf aur) khiska do, taake overlap na ho.
              {
                const ramadanBoxRect = { left: chosenX, right: chosenX + BOX_W, top: bTop, bottom: bBot };
                // ✅ Arrow/tail bhi ek obstacle hai — box ke neeche se bar tak
                // jaane wali connector line ke upar se bhi value ko hatao.
                const tailCX = Math.min(
                  Math.max(dataX, chosenX + ARROW_W / 2 + BR),
                  chosenX + BOX_W - ARROW_W / 2 - BR
                );
                const ramadanTailRect = {
                  left: Math.min(tailCX, dataX) - ARROW_W / 2 - 2,
                  right: Math.max(tailCX, dataX) + ARROW_W / 2 + 2,
                  top: bBot,
                  bottom: arrowTipY,
                };
                const ramadanObstacles = [ramadanBoxRect, ramadanTailRect];
                const singleClash = (a, b) =>
                  !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);
                const rectsClash = (a) => ramadanObstacles.some((r) => singleClash(a, r));

                const minY = chartTop + 10;
                const maxY = chartBottom - 10;
                const pushStep = 6;

                const nudgeAwayFromRamadan = () => {
                  pendingValueLabels.forEach((lbl) => {
                    if (lbl.finalY == null || isNaN(lbl.finalY)) return;

                    let boxX = lbl.x - lbl.width / 2;
                    let boxY = lbl.finalY - lbl.height / 2;
                    let lblRect = {
                      left: boxX - 6,
                      right: boxX + lbl.width + 6,
                      top: boxY - 2,
                      bottom: boxY + lbl.height + 6,
                    };
                    if (!rectsClash(lblRect)) return;

                    const isAbove = lbl.finalY < lbl.y; // label sits above its data point
                    let guard = 0;
                    while (rectsClash(lblRect) && guard < 80) {
                      let nextY = lbl.finalY + (isAbove ? -pushStep : pushStep);
                      // ✅ Clamp INSIDE the loop — if we clamp only after the
                      // loop, an over-push gets dragged back down into the
                      // very spot that was clashing, undoing the nudge.
                      nextY = Math.max(minY, Math.min(maxY, nextY));
                      if (nextY === lbl.finalY) break; // hit the bound, can't push further
                      lbl.finalY = nextY;
                      boxY = lbl.finalY - lbl.height / 2;
                      lblRect = {
                        left: boxX - 6,
                        right: boxX + lbl.width + 6,
                        top: boxY - 2,
                        bottom: boxY + lbl.height + 6,
                      };
                      guard++;
                    }

                    // ✅ Fallback: agar natural direction mein boundary aa gayi
                    // aur ab bhi clash hai, to opposite direction try karo.
                    if (rectsClash(lblRect)) {
                      const origFinalY = lbl.finalY;
                      let tryY = lbl.y; // reset near anchor, push the OTHER way
                      let tryBoxY = tryY - lbl.height / 2;
                      let tryRect = {
                        left: boxX - 6,
                        right: boxX + lbl.width + 6,
                        top: tryBoxY - 2,
                        bottom: tryBoxY + lbl.height + 6,
                      };
                      let g2 = 0;
                      while (rectsClash(tryRect) && g2 < 80) {
                        let nextY = tryY + (isAbove ? pushStep : -pushStep);
                        nextY = Math.max(minY, Math.min(maxY, nextY));
                        if (nextY === tryY) break;
                        tryY = nextY;
                        tryBoxY = tryY - lbl.height / 2;
                        tryRect = {
                          left: boxX - 6,
                          right: boxX + lbl.width + 6,
                          top: tryBoxY - 2,
                          bottom: tryBoxY + lbl.height + 6,
                        };
                        g2++;
                      }
                      // jo bhi kam clash kare / clash resolve kare, wahi rakho
                      if (!rectsClash(tryRect)) {
                        lbl.finalY = tryY;
                        boxY = tryBoxY;
                        lblRect = tryRect;
                      } else {
                        lbl.finalY = origFinalY;
                      }
                    }
                  });
                };

                // ✅ Nudging labels away from Ramadan can occasionally land
                // two of them on top of each other (both escaping into the
                // same free spot). A mutual-overlap pass among
                // pendingValueLabels themselves cleans that up — using the
                // same "whoever is physically higher gets pushed further up"
                // rule as the main resolution pass, so direction is always
                // correct regardless of which label is which.
                const resolveMutualOverlaps = () => {
                  const V_GAP = 4, H_GAP = 3;
                  const overlap2 = (a, b) =>
                    !(
                      (a.x + a.width / 2 + H_GAP) < (b.x - b.width / 2) ||
                      (a.x - a.width / 2 - H_GAP) > (b.x + b.width / 2) ||
                      (a.finalY + a.height / 2 + V_GAP) < (b.finalY - b.height / 2) ||
                      (a.finalY - a.height / 2 - V_GAP) > (b.finalY + b.height / 2)
                    );
                  let movedAny = false;
                  for (let pass = 0; pass < 40; pass++) {
                    let anyMoved = false;
                    for (let i = 0; i < pendingValueLabels.length; i++) {
                      for (let j = i + 1; j < pendingValueLabels.length; j++) {
                        const a = pendingValueLabels[i], b = pendingValueLabels[j];
                        if (a.finalY == null || b.finalY == null) continue;
                        if (!overlap2(a, b)) continue;
                        anyMoved = true;
                        movedAny = true;
                        const hi = a.finalY <= b.finalY ? a : b;
                        const lo = a.finalY <= b.finalY ? b : a;
                        const overlapAmt = (hi.finalY + hi.height / 2 + V_GAP) - (lo.finalY - lo.height / 2);
                        if (overlapAmt <= 0) continue;
                        hi.finalY = Math.max(minY, Math.min(maxY, hi.finalY - overlapAmt / 2));
                        lo.finalY = Math.max(minY, Math.min(maxY, lo.finalY + overlapAmt / 2));
                      }
                    }
                    if (!anyMoved) break;
                  }
                  return movedAny;
                };

                for (let round = 0; round < 3; round++) {
                  nudgeAwayFromRamadan();
                  const moved = resolveMutualOverlaps();
                  if (!moved) break;
                }
              }

              // ✅ Draw yahan nahi hota — sirf spec save karte hain.
              // Actual drawing drawRamadanBoxesPlugin karta hai, jo SABSE
              // AAKHIR mein (values ke baad) chalta hai, taake Ramadan
              // hamesha sab kuch ke upar, poora saaf dikhe.
              pendingRamadanBoxes.push({
                chosenX, chosenY, bTop, bBot, BOX_W, dataX, arrowTipY, label,
                color: RAMADAN_COLOR,
              });
            }
          });
        },
      }
      : { id: "ramadanPlugin_disabled", afterDraw() { } };

    // ✅ DRAW RAMADAN BOXES PLUGIN — draws the Ramadan callout boxes computed
    // above, LAST of all (after value labels), with a solid fill so no value
    // label can ever show through or cover the Ramadan text.
    const drawRamadanBoxesPlugin = {
      id: "drawRamadanBoxes",
      afterDatasetsDraw(chart) {
        const ctx = chart.ctx;
        const ARROW_W = 10;
        const BR = 3;

        pendingRamadanBoxes.forEach(({ chosenX, chosenY, bTop, bBot, BOX_W, dataX, arrowTipY, label, color }) => {
          const baseCX = Math.min(
            Math.max(dataX, chosenX + ARROW_W / 2 + BR),
            chosenX + BOX_W - ARROW_W / 2 - BR
          );
          const baseBL = baseCX - ARROW_W / 2;
          const baseBR = baseCX + ARROW_W / 2;

          ctx.save();

          ctx.beginPath();
          ctx.moveTo(chosenX + BR, bTop);
          ctx.lineTo(chosenX + BOX_W - BR, bTop);
          ctx.arcTo(chosenX + BOX_W, bTop, chosenX + BOX_W, bTop + BR, BR);
          ctx.lineTo(chosenX + BOX_W, bBot);
          ctx.lineTo(baseBR, bBot);
          ctx.lineTo(dataX, arrowTipY);
          ctx.lineTo(baseBL, bBot);
          ctx.lineTo(chosenX, bBot);
          ctx.lineTo(chosenX, bTop + BR);
          ctx.arcTo(chosenX, bTop, chosenX + BR, bTop, BR);
          ctx.closePath();

          ctx.shadowColor = color;
          ctx.shadowBlur = 10;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;
          ctx.strokeStyle = color;
          ctx.lineWidth = 1.5;
          ctx.stroke();
          ctx.shadowColor = "transparent";
          ctx.shadowBlur = 0;

          ctx.fillStyle = "#ffffff";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.font = "bold 10px Arial";
          ctx.fillText(label, chosenX + BOX_W / 2, (bTop + bBot) / 2);

          ctx.restore();
        });
      },
    };

    // ✅ alignTablePlugin — keeps the table's label column + total width lined up
    // with the chart's real plot-area pixels (measured from the actual canvas),
    // instead of relying on a guessed fixed width.
    const alignTablePlugin = {
      id: "alignTable",
      afterLayout(chart) {
        const area = chart.chartArea;
        if (!area || !containerRef.current || !tableRef.current) return;

        const canvasRect = containerRef.current.getBoundingClientRect();
        const tableRect = tableRef.current.getBoundingClientRect();

        const targetLeft = canvasRect.left + area.left;
        const targetRight = canvasRect.left + area.right;

        const labelWidth = Math.max(60, targetLeft - tableRect.left);
        const totalWidth = labelWidth + (targetRight - targetLeft);

        tableRef.current.style.width = `${totalWidth}px`;
        if (labelColRef.current) {
          labelColRef.current.style.width = `${labelWidth}px`;
        }
      },
    };

    // ✅ Build chart datasets
    const chartDatasets = datasets.map((ds) => ({
      label: ds.label,
      data: ds.data.map((val) => (val == null ? 0 : val)),
      type: ds.type || "line",
      borderColor: ds.color,
      backgroundColor: ds.color,
      pointBackgroundColor: ds.color,
      pointBorderColor: ds.color,
      pointStyle: "rect",
      pointRadius: 0,
      pointHoverRadius: 0,
      borderWidth: 1.5,
      tension: 0,
      borderDash: ds.borderDash || [],
      order: ds.order || 0,
    }));

    if (showBars && barDatasets.length > 0) {
      barDatasets.forEach((barDs) => {
        if (!barDs.data || barDs.data.length === 0) return;
        const hasValidData = barDs.data.some(
          (v) => v !== null && v !== undefined
        );
        if (!hasValidData) return;

        chartDatasets.push({
          label: barDs.label,
          data: barDs.data,
          type: "bar",
          backgroundColor: barDs.color || "rgba(150, 150, 150, 0.6)",
          borderColor: "black",//barDs.color || "rgba(150, 150, 150, 1)",
          borderWidth: 1,
          barPercentage: 0.5,
          categoryPercentage: 0.6,
          order: barDs.order || 0,
          legendHidden: true,
        });
      });
    }

    chartInstanceRef.current = new Chart.Chart(ctx, {
      type: "line",
      data: { labels: months, datasets: chartDatasets },
      options: {
        responsive: false,
        maintainAspectRatio: false,
        clip: false,
        devicePixelRatio: Math.max(window.devicePixelRatio || 1, 2),
        plugins: {
          legend: {
            display: true,
            position: "top",
            labels: {
              color: textColor,
              font: { size: 10 },
              usePointStyle: true,
              pointStyle: "line",
              padding: 6,
              boxWidth: 400,
              boxHeight: 20,
              filter: function (legendItem, chartData) {
                const dataset = chartData.datasets[legendItem.datasetIndex];
                return dataset.type !== "bar";
              },
            },
          },
          tooltip: {
            backgroundColor: "rgba(30, 41, 59, 0.95)",
            titleColor: "#fff",
            bodyColor: "#fff",
            borderColor: "#475569",
            borderWidth: 1,
            padding: 8,
            displayColors: true,
            callbacks: {
              label: function (context) {
                const val = context.parsed.y;
                return (
                  context.dataset.label +
                  ": " +
                  (typeof val === "number" ? val.toLocaleString() : val)
                );
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { display: false },
            border: { display: false },
            offset: true,
          },
          y: {
            min: yAxisRange.min,
            max: yAxisRange.max,
            clip: false,
            ticks: {
              color: textColor,
              font: { size: 9 },
              maxTicksLimit: 20,
              callback: function (value) {
                return typeof value === "number"
                  ? value.toLocaleString()
                  : value;
              },
            },
            grid: { display: false },
            border: { display: false },
            title: {
              display: true,
              text: yAxisTitle,
              color: textColor,
              font: { size: 10 },
            },
          },
        },
        layout: {
          padding: { left: 40, right: 5, top: 2, bottom: 45 },
        },
      },
      plugins: [lineShadowPlugin, barShadowPlugin, dataLabelsPlugin, ramadanPlugin, drawValueLabelsPlugin, drawRamadanBoxesPlugin, alignTablePlugin],
    });

     requestAnimationFrame(() => {
    if (containerRef.current && chartInstanceRef.current) {
       chartInstanceRef.current.resize(
         containerRef.current.offsetWidth,
         containerRef.current.offsetHeight
       );
     }
     });

    const resizeObserver = new ResizeObserver(() => {
  if (!containerRef.current || !chartInstanceRef.current) return;
  const w = containerRef.current.offsetWidth;
  const h = containerRef.current.offsetHeight;
  chartInstanceRef.current.resize(w, h);
});
if (containerRef.current) resizeObserver.observe(containerRef.current);

return () => {
  resizeObserver.disconnect();
  if (chartInstanceRef.current) {
    chartInstanceRef.current.destroy();
    chartInstanceRef.current = null;
  }
};
  }, [
    datasets,
    barDatasets,
    months,
    yAxisRange.min,
    yAxisRange.max,
    yAxisTitle,
    showBars,
    showRamadanRow,
    getRamadanMonths,
    ramadanborderColor,
    yearRanges,
    themeKey,
  ]);

  const allDatasets = [...datasets, ...(showBars ? barDatasets : [])];
  const tableDatasets = allDatasets.filter((ds) => !ds.hideInTable);

  return (
     <div className="w-full h-full flex items-center justify-center px-4">
      <div
        className="w-full mx-auto px-8 py-8 rounded-lg shadow-lg bg-card"
        style={{ backgroundColor: "#ffffff" }}
      >
        {/* Header Section */}
        <div
          className="text-center"
          style={{
            backgroundColor: "#ffffff",
            padding: "0.1rem 2rem 0.25rem 2rem",
          }}
        >
          {title && (
            <div style={{ margin: 0, lineHeight: 1.1 }}>
              <HeadingComponent variant={"main"}>{title}</HeadingComponent>
            </div>
          )}
          {subtitle && (
            <div style={{ margin: 0, lineHeight: 1.1 }}>
              <HeadingComponent variant={"sub"}>{subtitle}</HeadingComponent>
            </div>
          )}
        </div>

        {/* Chart + First Table */}
        <div
          className="w-full p-3"
          style={{ backgroundColor: "#464647", borderRadius: "8px" }}
        >
          {in_chart_content_line_1 && (
            <div className="text-center mt-2">
              <h3
                className="text-xs font-semibold tracking-wider"
                style={{ color: "#ffffff" }}
              >
                {in_chart_content_line_1}
              </h3>
            </div>
          )}

          <div
           className="ml-3 h-[350px] relative"

            ref={containerRef}
            style={{ marginBottom: "0px" }}
          >
            <canvas ref={chartRef}></canvas>
          </div>

          <div className="overflow-x-auto">
            <table ref={tableRef} className="!border-collapse text-xs table-fixed">
              <colgroup>
                <col ref={labelColRef} style={{ width: 80 }} />
                {months.map((_, idx) => (
                  <col key={idx} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th
                    className="!border-r py-0.5 px-1 text-center font-normal"
                    style={{ borderColor: "#ffffff" }}
                  ></th>
                  {months.map((month, idx) => (
                    <th
                      key={idx}
                      className="!border-r !border-t px-0.5 py-0.5 text-center font-normal text-[8px]"
                      style={{ color: "#ffffff", borderColor: "#ffffff" }}
                    >
                      {month}
                    </th>
                  ))}
                </tr>
                <tr>
                  <td
                    className="!border-r !border-b py-0.5 px-1 text-center text-[9px]"
                    style={{ borderColor: "#ffffff" }}
                  ></td>
                  {yearRanges.map((range, idx) => (
                    <td
                      key={idx}
                      colSpan={range.span}
                      className="!border !border-border px-0.5 py-0.5 text-center text-[9px]"
                      style={{ color: "#ffffff", borderColor: "#ffffff" }}
                    >
                      {range.year}
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableDatasets.map((dataset, dsIdx) => (
                  <tr key={dsIdx}>
                    <td
                      className="!border-l !border-r !border-b px-1 py-0.5 font-normal text-[7px] text-left"
                      style={{ color: "#ffffff", borderColor: "#ffffff" }}
                    >
                      <span
                        className="inline-block w-3 h-0.5 mr-1 align-middle"
                        style={{ backgroundColor: dataset.color }}
                      ></span>
                      {dataset.label}
                    </td>
                    {dataset.data.map((val, idx) => {
                      const displayValue = Array.isArray(val)
                        ? "-"
                        : val == null || val === undefined
                          ? "0"
                          : typeof val === "number"
                            ? val.toLocaleString()
                            : val;

                      return (
                        <td
                          key={idx}
                          className="!border-r !border-b px-0.5 py-[1px] text-center text-[8px]"
                          style={{
                            color: "#ffffff",
                            borderColor: "#ffffff",
                          }}
                        >
                          {displayValue}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <div
              className="text-center py-1 text-[9px]"
              style={{ color: "#ffffff" }}
            >
              Months
            </div>
          </div>
        </div>

        {/* Second Table */}
        {showSecondTable && secondTableData && (
          <div className="mt-4" style={{ backgroundColor: "#ffffff" }}>
            <table className="w-full !border-collapse text-xs">
              <thead>
                <tr>
                  <th
                    className="px-3 py-2 text-left text-[11px] font-bold"
                    style={{
                      backgroundColor: "#1a3a5c",
                      color: "#ffffff",
                      border: "1px solid #4a6fa5",
                      minWidth: "280px",
                      width: "35%",
                    }}
                  >
                    {secondTableData.title}
                  </th>
                  {[
                    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
                  ].map((m, idx) => (
                    <th
                      key={idx}
                      className="px-1 py-2 text-center text-[11px] font-bold"
                      style={{
                        backgroundColor: "#1a3a5c",
                        color: "#ffffff",
                        border: "1px solid #4a6fa5",
                        minWidth: "55px",
                      }}
                    >
                      {m}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {secondTableData.rows &&
                  secondTableData.rows.map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      <td
                        className="px-2 py-1.5 text-center text-[11px] font-bold"
                        style={{
                          backgroundColor: "#1a3a5c",
                          color: "#ffffff",
                          border: "1px solid #4a6fa5",
                        }}
                      >
                        {row.label}
                      </td>
                      {Array.from({ length: 12 }).map((_, idx) => {
                        const val = row.data[idx];
                        const displayVal =
                          val == null
                            ? "0%"
                            : typeof val === "number"
                              ? val.toLocaleString() + "%"
                              : val;
                        return (
                          <td
                            key={idx}
                            className="px-1 py-1.5 text-center text-[11px]"
                            style={{
                              backgroundColor: "#ffffff",
                              color: "#1a1a1a",
                              border: "1px solid #4a6fa5",
                              fontWeight: "400",
                            }}
                          >
                            {displayVal}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartTableComponent;